import React, { useState, useEffect } from 'react'
import {
  MagnifyingGlassIcon,
  UserIcon,
  AcademicCapIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  FunnelIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  VideoCameraIcon,
  StarIcon,
  TagIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { usersApi, teacherApplicationsApi } from '../../services/adminApi'
import StudentMemberSettings from './StudentMemberSettings'

// 国家代码到中文名称映射
const countryNameMap: Record<string, string> = {
  // 亚洲
  'CN': '中国',
  'JP': '日本',
  'KR': '韩国',
  'SG': '新加坡',
  'MY': '马来西亚',
  'TH': '泰国',
  'VN': '越南',
  'PH': '菲律宾',
  'ID': '印度尼西亚',
  'IN': '印度',
  'BD': '孟加拉国',
  'PK': '巴基斯坦',
  'LK': '斯里兰卡',
  'NP': '尼泊尔',
  'MM': '缅甸',
  'KH': '柬埔寨',
  'LA': '老挝',
  'BN': '文莱',
  'MN': '蒙古',
  'KZ': '哈萨克斯坦',
  'UZ': '乌兹别克斯坦',
  'AF': '阿富汗',
  'IR': '伊朗',
  'IQ': '伊拉克',
  'KW': '科威特',
  'QA': '卡塔尔',
  'BH': '巴林',
  'OM': '阿曼',
  'YE': '也门',
  'JO': '约旦',
  'LB': '黎巴嫩',
  'SY': '叙利亚',
  'PS': '巴勒斯坦',
  // 北美洲
  'US': '美国',
  'CA': '加拿大',
  'MX': '墨西哥',
  // 欧洲
  'GB': '英国',
  'UK': '英国',
  'DE': '德国',
  'FR': '法国',
  'IT': '意大利',
  'ES': '西班牙',
  'PT': '葡萄牙',
  'NL': '荷兰',
  'BE': '比利时',
  'CH': '瑞士',
  'AT': '奥地利',
  'SE': '瑞典',
  'NO': '挪威',
  'DK': '丹麦',
  'FI': '芬兰',
  'IE': '爱尔兰',
  'PL': '波兰',
  'RU': '俄罗斯',
  'UA': '乌克兰',
  'GR': '希腊',
  'CZ': '捷克',
  'HU': '匈牙利',
  'RO': '罗马尼亚',
  'BG': '保加利亚',
  'HR': '克罗地亚',
  'SK': '斯洛伐克',
  'SI': '斯洛文尼亚',
  'RS': '塞尔维亚',
  'BY': '白俄罗斯',
  'LT': '立陶宛',
  'LV': '拉脱维亚',
  'EE': '爱沙尼亚',
  // 大洋洲
  'AU': '澳大利亚',
  'NZ': '新西兰',
  'FJ': '斐济',
  // 南美洲
  'BR': '巴西',
  'AR': '阿根廷',
  'CL': '智利',
  'CO': '哥伦比亚',
  'PE': '秘鲁',
  'VE': '委内瑞拉',
  'EC': '厄瓜多尔',
  'BO': '玻利维亚',
  'PY': '巴拉圭',
  'UY': '乌拉圭',
  // 非洲
  'ZA': '南非',
  'EG': '埃及',
  'NG': '尼日利亚',
  'KE': '肯尼亚',
  'GH': '加纳',
  'ET': '埃塞俄比亚',
  'TZ': '坦桑尼亚',
  'MA': '摩洛哥',
  'DZ': '阿尔及利亚',
  'TN': '突尼斯',
  // 中东
  'AE': '阿联酋',
  'SA': '沙特阿拉伯',
  'IL': '以色列',
  'TR': '土耳其',
  // 中国特别行政区
  'TW': '中国台湾（China Taiwan）',
  'HK': '中国香港（China Hong Kong）',
  'MO': '中国澳门（China Macau）',
  // 英文名称映射
  'China': '中国',
  'United States': '美国',
  'United Kingdom': '英国',
  'Canada': '加拿大',
  'Australia': '澳大利亚',
  'New Zealand': '新西兰',
  'Japan': '日本',
  'South Korea': '韩国',
  'Singapore': '新加坡',
  'Malaysia': '马来西亚',
  'Thailand': '泰国',
  'Vietnam': '越南',
  'Philippines': '菲律宾',
  'Indonesia': '印度尼西亚',
  'India': '印度',
  'Bangladesh': '孟加拉国',
  'Pakistan': '巴基斯坦',
  'Germany': '德国',
  'France': '法国',
  'Italy': '意大利',
  'Spain': '西班牙',
  'Portugal': '葡萄牙',
  'Netherlands': '荷兰',
  'Belgium': '比利时',
  'Switzerland': '瑞士',
  'Austria': '奥地利',
  'Sweden': '瑞典',
  'Norway': '挪威',
  'Denmark': '丹麦',
  'Finland': '芬兰',
  'Ireland': '爱尔兰',
  'Poland': '波兰',
  'Russia': '俄罗斯',
  'Ukraine': '乌克兰',
  'Brazil': '巴西',
  'Mexico': '墨西哥',
  'Argentina': '阿根廷',
  'Chile': '智利',
  'South Africa': '南非',
  'Egypt': '埃及',
  'Nigeria': '尼日利亚',
  'United Arab Emirates': '阿联酋',
  'Saudi Arabia': '沙特阿拉伯',
  'Israel': '以色列',
  'Turkey': '土耳其',
  'Greece': '希腊',
  'Czech Republic': '捷克',
  'Hungary': '匈牙利',
  'Romania': '罗马尼亚',
  'Taiwan': '中国台湾（China Taiwan）',
  'Hong Kong': '中国香港（China Hong Kong）',
  'Macau': '中国澳门（China Macau）'
}

// 获取国家中文名称
const getCountryName = (countryCode: string | undefined): string => {
  if (!countryCode) return '-'
  // 先尝试直接匹配
  if (countryNameMap[countryCode]) {
    return countryNameMap[countryCode]
  }
  // 尝试忽略大小写匹配
  const upperCode = countryCode.toUpperCase()
  if (countryNameMap[upperCode]) {
    return countryNameMap[upperCode]
  }
  // 如果没有匹配，返回原始值
  return countryCode
}

// Version: 2025-12-03-fix-object-rendering
// 标签页类型
type TabType = 'all' | 'students' | 'teachers' | 'pending_teachers' | 'approved_teachers' | 'rejected_teachers' | 'admins' | 'referrers'

// 用户接口定义（扩展以支持教师字段）
interface User {
  id: string
  user_id?: string
  application_id?: string
  email: string
  full_name: string
  phone?: string
  avatar_url?: string
  profile_photo_url?: string
  user_type: 'student' | 'teacher' | 'admin' | 'referrer'
  status: 'pending' | 'active' | 'suspended' | 'banned'
  approval_status?: 'pending' | 'approved' | 'rejected'
  email_verified?: boolean
  phone_verified?: boolean
  country?: string
  timezone?: string
  created_at: string
  updated_at?: string
  last_login?: string
  last_login_ip?: string
  profile?: {
    rating?: number
    total_reviews?: number
    balance_hours?: number
    total_lessons?: number
  }

  // 教师专属字段
  bio?: string
  education?: string
  video_url?: string
  intro_video_url?: string
  video_duration?: number
  video_size?: number
  certifications?: any
  teaching_experience_years?: number
  specialties?: string[]
  native_language?: string
  teaching_languages?: string[]
  target_student_languages?: string[]
  expected_hourly_rate?: number
  rejection_reason?: string
  admin_notes?: string
  teacher_level?: number
  rating?: number
  total_reviews?: number
  total_lessons?: number
  completion_rate?: number
  has_video?: boolean
  video_info?: {
    url: string
    duration: number
    size: number
  }
  payment_method?: string
  payment_account?: string
  speaks_chinese?: string
  chinese_proficiency?: string
}

interface UserListResponse {
  users: User[]
  pagination: {
    page: number
    limit: number
    total: number
    hasMore: boolean
  }
}

interface UserDetailResponse {
  user: User
  profile: any
  stats: {
    total_bookings?: number
    total_teaching_sessions?: number
    total_payments?: number
  }
}

export const UserManagement: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [userDetail, setUserDetail] = useState<UserDetailResponse | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createForm, setCreateForm] = useState({
    email: '',
    phone: '',
    full_name: '',
    password: '',
    user_type: 'teacher' as 'student' | 'teacher' | 'admin' | 'referrer'
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // 标签页状态
  const [activeTab, setActiveTab] = useState<TabType>('all')

  // 教师审核模态框
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)

  // 审核表单
  const [approveForm, setApproveForm] = useState({
    teacher_level: 1,
    admin_notes: '',
    set_hourly_rate: 0,
    categories: [] as string[]
  })

  // 业务分类定义
  const BUSINESS_CATEGORIES = [
    { id: 'starting', name: '启航' },
    { id: 'advanced', name: '进阶' },
    { id: 'professional', name: '精研' },
    { id: 'elite', name: '致远' }
  ]

  const [rejectForm, setRejectForm] = useState({
    rejection_reason: '',
    admin_notes: '',
    allow_reapply: true
  })

  // 搜索和筛选
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // 保存滚动位置
  const [scrollPosition, setScrollPosition] = useState(0)

  // 管理员备注编辑状态
  const [editingAdminNotes, setEditingAdminNotes] = useState(false)
  const [adminNotesInput, setAdminNotesInput] = useState('')
  const [savingAdminNotes, setSavingAdminNotes] = useState(false)

  // 教师业务分类管理状态
  const [editingCategories, setEditingCategories] = useState(false)
  const [categoriesInput, setCategoriesInput] = useState<string[]>([])
  const [savingCategories, setSavingCategories] = useState(false)

  // 备份状态
  const [backupLoading, setBackupLoading] = useState(false)

  // 教师费率编辑状态
  const [editingRates, setEditingRates] = useState(false)
  const [ratesForm, setRatesForm] = useState({
    hourly_rate: 0,
    student_rate_per_25min: 0,
    is_special_price: false // 是否特价教师
  })
  const [savingRates, setSavingRates] = useState(false)

  // 分页
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false
  })

  // 加载用户列表（整合用户和教师数据）
  const loadUsers = async (page = 1) => {
    try {
      setLoading(true)
      let combinedUsers: User[] = []
      let totalCount = 0

      // 根据标签页加载不同数据
      if (activeTab === 'all' || activeTab === 'students' || activeTab === 'admins' || activeTab === 'referrers') {
        // 加载普通用户（排除教师类型，避免重复）
        const userType = activeTab === 'students' ? 'student'
          : activeTab === 'admins' ? 'admin'
            : activeTab === 'referrers' ? 'referrer'
              : filterType || ''

        const userResponse = await usersApi.getUsers({
          search: searchTerm,
          type: userType,
          status: filterStatus,
          page,
          limit: pagination.limit,
          sort_by: sortBy,
          sort_order: sortOrder
        })

        if (userResponse.success) {
          const usersData = userResponse.data.users || userResponse.data || []
          // 获取分页信息中的总数
          const userPagination = userResponse.data.pagination || userResponse.pagination
          if (userPagination && userPagination.total !== undefined) {
            totalCount += userPagination.total
          } else {
            totalCount += usersData.length
          }
          // 如果是"全部用户"标签页，过滤掉教师类型，避免重复
          if (activeTab === 'all') {
            combinedUsers = usersData.filter((u: any) => u.user_type !== 'teacher')
          } else {
            combinedUsers = [...usersData]
          }
        }
      }

      // 加载教师申请数据（使用教师申请接口，包含完整注册信息）
      if (activeTab === 'all' || activeTab === 'teachers' || activeTab.includes('teachers')) {
        const teacherStatus = activeTab === 'pending_teachers' ? 'pending'
          : activeTab === 'approved_teachers' ? 'approved'
            : activeTab === 'rejected_teachers' ? 'rejected'
              : ''

        try {
          const teacherResponse = await teacherApplicationsApi.getApplications({
            page,
            limit: pagination.limit,
            status: teacherStatus || undefined,
            search: searchTerm || undefined,
            sort_by: sortBy,
            sort_order: sortOrder
          })

          if (teacherResponse.success) {
            const teachersData = teacherResponse.data || []
            // 获取教师分页信息中的总数
            const teacherPagination = teacherResponse.pagination
            // 注意："全部用户"标签页不累加教师总数，因为usersApi已包含所有用户
            if (activeTab !== 'all') {
              // 仅显示教师相关标签页时，使用教师的总数
              if (teacherPagination && teacherPagination.total !== undefined) {
                totalCount = teacherPagination.total
              } else {
                totalCount = teachersData.length
              }
            }
            // 将教师数据转换为统一格式，保留所有注册信息
            const mappedTeachers = teachersData.map((t: any) => ({
              ...t,
              user_type: 'teacher' as const,
              status: t.user_status || (t.approval_status === 'approved' ? 'active' : 'pending'),
              last_login: t.last_login_at || t.last_login,
              avatar_url: t.profile_photo_url || t.avatar_url,
              // 确保ID字段统一：id 为用户ID，application_id 为教师档案ID
              id: t.user_id || t.id,
              application_id: t.id
            }))

            // 合并数据
            if (activeTab === 'all' || activeTab === 'teachers') {
              combinedUsers = [...combinedUsers, ...mappedTeachers]
            } else {
              combinedUsers = mappedTeachers
            }
          }
        } catch (error) {
          console.error('加载教师数据失败:', error)
        }
      }

      setUsers(combinedUsers)
      // 计算是否还有更多数据
      const totalPages = Math.ceil(totalCount / pagination.limit)
      setPagination({
        ...pagination,
        page,
        total: totalCount,
        hasMore: page < totalPages
      })

    } catch (error) {
      console.error('加载用户数据失败:', error)
      toast.error('加载用户数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 获取用户详情
  const loadUserDetail = async (userId: string, teacherProfileId?: string) => {
    try {
      setLoading(true)

      // 先尝试从当前用户列表中找到该用户
      const currentUser = users.find(u => (u.id === userId || u.user_id === userId))

      // 如果提供了教师档案ID，或者当前用户是教师
      const finalTeacherProfileId = teacherProfileId || (currentUser?.user_type === 'teacher' ? (currentUser.application_id || currentUser.id) : undefined)

      // 如果是教师，使用教师申请接口获取完整信息
      if (finalTeacherProfileId && (currentUser?.user_type === 'teacher' || !currentUser)) {
        try {
          const teacherResponse = await teacherApplicationsApi.getApplicationDetail(finalTeacherProfileId)
          if (teacherResponse.success) {
            // 将教师申请数据转换为用户详情格式
            setUserDetail({
              user: {
                ...teacherResponse.data,
                id: teacherResponse.data.user_id, // 确保使用用户 ID 而非教师档案 ID
                application_id: teacherResponse.data.id, // 保存档案 ID
                user_type: 'teacher',
                status: teacherResponse.data.approval_status === 'approved' ? 'active' : 'pending',
                avatar_url: teacherResponse.data.profile_photo_url || teacherResponse.data.avatar_url
              },
              profile: teacherResponse.data, // 教师申请数据包含所有注册信息
              stats: {
                total_bookings: teacherResponse.data.booking_stats?.total_bookings || 0,
                total_teaching_sessions: teacherResponse.data.booking_stats?.completed_bookings || 0,
                total_payments: 0
              }
            })
            setSelectedUser({
              ...teacherResponse.data,
              id: teacherResponse.data.user_id, // 用户 ID
              application_id: teacherResponse.data.id, // 档案 ID
              user_type: 'teacher',
              status: teacherResponse.data.approval_status === 'approved' ? 'active' : 'pending'
            })
            setShowDetailModal(true)
            return
          }
        } catch (error) {
          console.error('获取教师详情失败，尝试使用普通用户接口:', error)
        }
      }

      // 对于非教师或教师接口失败，使用普通用户接口
      const response = await usersApi.getUserById(userId)
      console.log('👉 User Detail Response:', response)
      if (response.success) {
        console.log('👉 User Detail Data:', response.data)
        console.log('👉 Profile Data:', response.data.profile)
        setUserDetail(response.data)
        setSelectedUser(response.data.user)
        setShowDetailModal(true)
      }
    } catch (error) {
      console.error('获取用户详情失败:', error)
      toast.error('获取用户详情失败')
    } finally {
      setLoading(false)
    }
  }

  // 更新用户状态
  const handleStatusUpdate = async () => {
    if (!selectedUser || !newStatus) return

    try {
      const userId = (selectedUser.user_id || selectedUser.id).toString()
      let applicationId = (selectedUser as any).application_id

      // 如果是教师但 applicationId 不存在，尝试查询获取
      if (selectedUser.user_type === 'teacher' && !applicationId) {
        console.log('⚠️ 教师状态更新：application_id 不存在，尝试通过邮箱搜索获取...')
        try {
          const searchResponse = await teacherApplicationsApi.getApplications({
            search: selectedUser.email,
            limit: 10
          })
          if (searchResponse.success && searchResponse.data && searchResponse.data.length > 0) {
            const matchedTeacher = searchResponse.data.find(
              (t: any) => t.email === selectedUser.email || t.user_id?.toString() === userId
            )
            if (matchedTeacher) {
              applicationId = matchedTeacher.id
              console.log('✅ 找到教师档案ID:', applicationId)
            }
          }
        } catch (searchError) {
          console.error('搜索教师档案失败:', searchError)
        }
      }

      let response
      // 如果是教师且有 applicationId，使用专门的教师状态更新接口
      if (selectedUser.user_type === 'teacher' && applicationId) {
        response = await teacherApplicationsApi.updateTeacherStatus(applicationId, {
          status: newStatus as 'active' | 'suspended' | 'banned',
          reason: '管理员手动更新状态'
        })
      } else {
        // 普通用户或未找到教师档案的用户，使用通用接口
        response = await usersApi.updateUserStatus(userId, newStatus)
      }

      if (response.success) {
        toast.success('用户状态已更新')
        setShowStatusModal(false)
        loadUsers(pagination.page)
        if (showDetailModal) {
          loadUserDetail(userId, applicationId)
        }
      }
    } catch (error) {
      console.error('更新用户状态失败:', error)
      toast.error('更新用户状态失败')
    }
  }

  // 批准教师申请
  const handleApproveTeacher = async () => {
    if (!selectedUser) return

    console.log('\n========== 📧 前端：开始批准教师申请 ==========')
    console.log('selectedUser:', selectedUser)
    console.log('selectedUser.id:', selectedUser.id)
    console.log('selectedUser.application_id:', (selectedUser as any).application_id)

    // 教师审核接口需要教师档案ID (teacher_profile_id / application_id)
    let applicationId = (selectedUser as any).application_id

    // 如果 application_id 不存在，需要先查询获取教师档案ID
    if (!applicationId) {
      console.log('⚠️ application_id 不存在，尝试通过邮箱搜索获取教师档案ID...')
      try {
        // 通过邮箱搜索教师申请
        const searchResponse = await teacherApplicationsApi.getApplications({
          search: selectedUser.email,
          limit: 10
        })
        if (searchResponse.success && searchResponse.data && searchResponse.data.length > 0) {
          // 找到匹配的教师档案
          const matchedTeacher = searchResponse.data.find(
            (t: any) => t.email === selectedUser.email || t.user_id?.toString() === selectedUser.id?.toString()
          )
          if (matchedTeacher) {
            applicationId = matchedTeacher.id
            console.log('✅ 找到教师档案ID:', applicationId)
          }
        }
      } catch (searchError) {
        console.error('搜索教师档案失败:', searchError)
      }

      if (!applicationId) {
        console.error('❌ 无法获取教师档案ID')
        toast.error('无法获取教师档案ID，请刷新页面后重试')
        return
      }
    }

    console.log('申请ID:', applicationId)
    console.log('教师邮箱:', selectedUser.email)
    console.log('教师姓名:', selectedUser.full_name)
    console.log('审批表单数据:', approveForm)

    try {
      console.log('调用 API: teacherApplicationsApi.approveApplication')
      const response = await teacherApplicationsApi.approveApplication(
        applicationId,
        approveForm
      )
      console.log('API 响应:', response)

      if (response.success) {
        console.log('✅ 批准成功！后端应该已发送邮件到:', selectedUser.email)
        toast.success('教师申请已批准')
        setShowApproveModal(false)
        setShowDetailModal(false)
        loadUsers(pagination.page)
      } else {
        console.error('❌ 批准失败，响应:', response)
        toast.error(response?.error?.message || '批准教师申请失败')
      }
    } catch (error) {
      console.error('💥 批准教师申请异常:', error)
      console.error('错误详情:', JSON.stringify(error, Object.getOwnPropertyNames(error)))
      const message = error instanceof Error ? error.message : '批准教师申请失败'
      toast.error(message)
    }
    console.log('========== 📧 前端：批准流程结束 ==========\n')
  }

  // 拒绝教师申请
  const handleRejectTeacher = async () => {
    if (!selectedUser || !rejectForm.rejection_reason) {
      toast.error('请填写拒绝原因')
      return
    }

    console.log('\n========== 前端：开始拒绝教师申请 ==========')
    console.log('selectedUser:', selectedUser)
    console.log('selectedUser.id:', selectedUser.id)
    console.log('selectedUser.application_id:', (selectedUser as any).application_id)

    // 教师审核接口需要教师档案ID (teacher_profile_id / application_id)
    let applicationId = (selectedUser as any).application_id

    // 如果 application_id 不存在，需要先查询获取教师档案ID
    if (!applicationId) {
      console.log('⚠️ application_id 不存在，尝试通过邮箱搜索获取教师档案ID...')
      try {
        // 通过邮箱搜索教师申请
        const searchResponse = await teacherApplicationsApi.getApplications({
          search: selectedUser.email,
          limit: 10
        })
        if (searchResponse.success && searchResponse.data && searchResponse.data.length > 0) {
          // 找到匹配的教师档案
          const matchedTeacher = searchResponse.data.find(
            (t: any) => t.email === selectedUser.email || t.user_id?.toString() === selectedUser.id?.toString()
          )
          if (matchedTeacher) {
            applicationId = matchedTeacher.id
            console.log('✅ 找到教师档案ID:', applicationId)
          }
        }
      } catch (searchError) {
        console.error('搜索教师档案失败:', searchError)
      }

      if (!applicationId) {
        console.error('❌ 无法获取教师档案ID')
        toast.error('无法获取教师档案ID，请刷新页面后重试')
        return
      }
    }

    console.log('申请ID:', applicationId)

    try {
      const response = await teacherApplicationsApi.rejectApplication(
        applicationId,
        rejectForm
      )
      if (response.success) {
        toast.success('教师申请已拒绝')
        setShowRejectModal(false)
        setShowDetailModal(false)
        loadUsers(pagination.page)
      }
    } catch (error) {
      console.error('拒绝教师申请失败:', error)
      toast.error('拒绝教师申请失败')
    }
    console.log('========== 前端：拒绝流程结束 ==========\n')
  }

  // 删除用户
  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      const userId = selectedUser.user_id || selectedUser.id
      const response = await usersApi.deleteUser(userId)
      if (response.success) {
        toast.success('用户已删除')
        setShowDeleteConfirm(false)
        setShowDetailModal(false)
        loadUsers(pagination.page)
      }
    } catch (error) {
      console.error('删除用户失败:', error)
      toast.error('删除用户失败')
    }
  }

  // 保存管理员备注
  const handleSaveAdminNotes = async () => {
    if (!userDetail) return

    try {
      setSavingAdminNotes(true)
      const userId = userDetail.user.id
      const response = await usersApi.updateAdminNotes(userId, adminNotesInput)
      if (response.success) {
        toast.success('备注已保存')
        setEditingAdminNotes(false)
        // 更新本地状态
        setUserDetail({
          ...userDetail,
          user: {
            ...userDetail.user,
            admin_notes: adminNotesInput
          }
        })
      }
    } catch (error) {
      console.error('保存备注失败:', error)
      toast.error('保存备注失败')
    } finally {
      setSavingAdminNotes(false)
    }
  }

  // 开始编辑管理员备注
  const handleStartEditAdminNotes = () => {
    if (userDetail) {
      setAdminNotesInput(userDetail.user.admin_notes || '')
      setEditingAdminNotes(true)
    }
  }

  // 取消编辑管理员备注
  const handleCancelEditAdminNotes = () => {
    setEditingAdminNotes(false)
    setAdminNotesInput('')
  }

  // 教师业务分类管理
  const handleStartEditCategories = () => {
    if (!userDetail?.profile) return
    let currentCats: string[] = []
    try {
      const catsData = (userDetail.profile as any).categories
      if (catsData) {
        currentCats = typeof catsData === 'string' ? JSON.parse(catsData) : catsData
      }
    } catch (e) {
      console.error('解析教师分类失败:', e)
    }
    setCategoriesInput(currentCats || [])
    setEditingCategories(true)
  }

  const handleCancelEditCategories = () => {
    setEditingCategories(false)
    setCategoriesInput([])
  }

  const handleSaveCategories = async () => {
    if (!selectedUser || !userDetail?.profile) return
    const applicationId = (userDetail.profile as any).id
    setSavingCategories(true)
    try {
      const response = await teacherApplicationsApi.updateTeacherCategories(applicationId.toString(), categoriesInput)
      if (response.success) {
        toast.success('业务分类已更新')
        setEditingCategories(false)
        // 更新本地数据
        if (userDetail) {
          setUserDetail({
            ...userDetail,
            profile: { ...userDetail.profile, categories: categoriesInput } as any
          })
        }
      }
    } catch (error) {
      console.error('更新业务分类失败:', error)
      toast.error('更新业务分类失败')
    } finally {
      setSavingCategories(false)
    }
  }

  // 开始编辑教师费率
  const handleStartEditRates = () => {
    if (userDetail && userDetail.profile) {
      setRatesForm({
        hourly_rate: (userDetail.profile as any).hourly_rate || 0,
        student_rate_per_25min: (userDetail.profile as any).student_rate_per_25min || 0,
        is_special_price: (userDetail.profile as any).is_special_price === 1
      })
      setEditingRates(true)
    }
  }

  // 取消编辑教师费率
  const handleCancelEditRates = () => {
    setEditingRates(false)
    setRatesForm({ hourly_rate: 0, student_rate_per_25min: 0, is_special_price: false })
  }

  // 保存教师费率
  const handleSaveRates = async () => {
    if (!userDetail || !userDetail.profile) return

    try {
      setSavingRates(true)
      const teacherId = (userDetail.profile as any).id
      const response = await teacherApplicationsApi.updateTeacherRates(teacherId, ratesForm)
      if (response.success) {
        toast.success('费率已保存')
        setEditingRates(false)
        // 更新本地状态
        setUserDetail({
          ...userDetail,
          profile: {
            ...userDetail.profile,
            hourly_rate: ratesForm.hourly_rate,
            student_rate_per_25min: ratesForm.student_rate_per_25min
          }
        })
      }
    } catch (error) {
      console.error('保存费率失败:', error)
      toast.error('保存费率失败')
    } finally {
      setSavingRates(false)
    }
  }

  // 备份所有用户
  const handleBackupUsers = async () => {
    if (!confirm('将导出CSV文件并获取用户相关资源文件列表（头像、视频、图片），是否继续？')) {
      return
    }

    try {
      setBackupLoading(true)

      // Step 1: 下载CSV数据
      const csvResponse = await usersApi.exportUsers()
      if (csvResponse.success && csvResponse.data) {
        // 下载CSV文件
        const blob = new Blob([csvResponse.data], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `users_backup_${new Date().toISOString().split('T')[0]}.csv`
        link.click()
        URL.revokeObjectURL(url)

        toast.success('CSV文件已下载')
      }

      // Step 2: 请求资源文件列表
      const resourceResponse = await usersApi.exportUserResources()
      if (resourceResponse.success && resourceResponse.data) {
        const { file_count, files, message } = resourceResponse.data

        // 生成文件列表文本
        const fileListContent = [
          '用户资源文件列表',
          `总计: ${file_count} 个文件`,
          '',
          ...files
        ].join('\n')

        // 下载文件列表
        const blob = new Blob([fileListContent], { type: 'text/plain;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `user_resources_list_${new Date().toISOString().split('T')[0]}.txt`
        link.click()
        URL.revokeObjectURL(url)

        toast.success(`资源文件列表已下载（${file_count}个文件）`)

        if (message) {
          setTimeout(() => {
            alert(message)
          }, 500)
        }
      }

      toast.success('备份完成！')
    } catch (error) {
      console.error('备份失败:', error)
      toast.error('备份失败，请稍后重试')
    } finally {
      setBackupLoading(false)
    }
  }

  // 格式化日期
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('zh-CN')
  }

  // 格式化金额
  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`
  }

  // 获取状态样式
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'suspended':
        return 'bg-red-100 text-red-800'
      case 'banned':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return '活跃'
      case 'pending':
        return '待审核'
      case 'suspended':
        return '已暂停'
      case 'banned':
        return '已封禁'
      default:
        return status
    }
  }

  // 获取用户类型样式
  const getUserTypeStyle = (type: string) => {
    switch (type) {
      case 'teacher':
        return 'bg-green-100 text-green-800'
      case 'student':
        return 'bg-blue-100 text-blue-800'
      case 'admin':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // 获取用户类型文本
  const getUserTypeText = (type: string) => {
    switch (type) {
      case 'teacher':
        return '教师'
      case 'student':
        return '学生'
      case 'admin':
        return '管理员'
      default:
        return type
    }
  }

  // 副作用
  useEffect(() => {
    loadUsers()
  }, [searchTerm, filterType, filterStatus, sortBy, sortOrder, activeTab])

  // 标签页切换时重置页码
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [activeTab])

  return (
    <div>
      {/* 页面标题和添加用户按钮 */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">用户管理</h1>
          <p className="mt-2 text-gray-600">统一管理所有用户、教师和申请审核</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => handleBackupUsers()}
            disabled={backupLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {backupLoading ? (
              <>
                <ArrowPathIcon className="-ml-1 mr-2 h-5 w-5 animate-spin" />
                备份中...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5" />
                备份所有用户
              </>
            )}
          </button>
          <button
            onClick={() => {
              setCreateForm({ email: '', full_name: '', user_type: 'student' })
              setShowCreateModal(true)
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            添加用户
          </button>
        </div>
      </div>

      {/* 标签页 */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 overflow-x-auto" aria-label="用户分类">
            {[
              { key: 'all', label: '全部用户', icon: UserIcon },
              { key: 'students', label: '学生', icon: UserIcon },
              { key: 'teachers', label: '教师', icon: AcademicCapIcon },
              { key: 'pending_teachers', label: '待审核教师', icon: ClockIcon },
              { key: 'approved_teachers', label: '已批准教师', icon: CheckCircleIcon },
              { key: 'rejected_teachers', label: '已拒绝教师', icon: XCircleIcon },
              { key: 'admins', label: '管理员', icon: UserGroupIcon },
              { key: 'referrers', label: '推广员', icon: UserIcon }
            ].map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as TabType)}
                  className={`flex items-center px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${isActive
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* 搜索框 */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="搜索用户姓名、邮箱..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>

          {/* 筛选选项 */}
          <div className="flex gap-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
            >
              <option value="">全部类型</option>
              <option value="student">学生</option>
              <option value="teacher">教师</option>
              <option value="admin">管理员</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
            >
              <option value="">全部状态</option>
              <option value="active">活跃</option>
              <option value="pending">待审核</option>
              <option value="suspended">已暂停</option>
              <option value="banned">已封禁</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
            >
              <option value="id">ID</option>
              <option value="created_at">注册时间</option>
              <option value="last_login">最后登录</option>
              <option value="full_name">姓名</option>
              <option value="email">邮箱</option>
            </select>

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
            >
              <option value="desc">降序</option>
              <option value="asc">升序</option>
            </select>

            {/* 刷新按钮 */}
            <button
              onClick={() => loadUsers()}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* 统计信息 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">总用户数</p>
              <p className="text-xl font-bold text-gray-900">{pagination.total}</p>
            </div>
            <UserIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">活跃用户</p>
              <p className="text-xl font-bold text-green-600">
                {users.filter(u => u.status === 'active').length}
              </p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">教师</p>
              <p className="text-xl font-bold text-blue-600">
                {users.filter(u => u.user_type === 'teacher').length}
              </p>
            </div>
            <AcademicCapIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">学生</p>
              <p className="text-xl font-bold text-purple-600">
                {users.filter(u => u.user_type === 'student').length}
              </p>
            </div>
            <UserIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* 用户列表 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  用户信息
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  类型
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  管理员备注
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  登录IP / 最后登录
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    暂无用户数据
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs font-medium text-gray-900">
                      {user.id}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          {user.avatar_url ? (
                            <img
                              className="h-8 w-8 rounded-full"
                              src={user.avatar_url}
                              alt={user.full_name}
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-purple-600 text-xs font-medium">
                                {user.full_name.charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-2">
                          <div className="text-xs font-medium text-gray-900 truncate max-w-[120px]" title={user.full_name}>
                            {user.full_name}
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-[120px]" title={user.email}>
                            {user.email}
                          </div>
                          {user.phone && (
                            <div className="text-xs text-gray-400 truncate max-w-[120px]" title={user.phone}>
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getUserTypeStyle(user.user_type)}`}>
                        {getUserTypeText(user.user_type)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusStyle(user.status)}`}>
                        {getStatusText(user.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-xs text-gray-600 truncate max-w-[150px]" title={user.admin_notes || '-'}>
                        {user.admin_notes ? user.admin_notes.substring(0, 10) + (user.admin_notes.length > 10 ? '...' : '') : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      <div>
                        <div className="text-gray-900">{user.last_login_ip || '-'}</div>
                        <div className="text-xs text-gray-400">{formatDate(user.last_login || user.last_login_at)}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            // 保存当前滚动位置
                            setScrollPosition(window.scrollY)
                            loadUserDetail(user.id, (user as any).application_id)
                          }}
                          className="text-gray-600 hover:text-gray-900"
                          title="查看详情"
                        >
                          <EyeIcon className="h-3.5 w-3.5" />
                        </button>

                        {/* 教师审核按钮 */}
                        {user.user_type === 'teacher' && user.approval_status === 'pending' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedUser(user)
                                setShowApproveModal(true)
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="批准申请"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user)
                                setShowRejectModal(true)
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="拒绝申请"
                            >
                              <XCircleIcon className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}

                        <button
                          onClick={() => {
                            setSelectedUser(user)
                            setNewStatus(user.status === 'active' ? 'suspended' : 'active')
                            setShowStatusModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="更改状态"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={async () => {
                            if (user.email === 'admin@talknect.com') {
                              toast.error('系统管理员账号不能删除！')
                              return
                            }

                            if (!confirm(`确定要删除用户 ${user.full_name} (${user.email}) 吗？此操作不可恢复！`)) {
                              return
                            }

                            const userId = user.user_id || user.id
                            await usersApi.deleteUser(userId);
                            toast.success('用户已删除');
                            loadUsers(pagination.page)
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="删除用户"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              显示 {((pagination.page - 1) * pagination.limit) + 1} 到{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} 条，
              共 {pagination.total} 条记录
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => loadUsers(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <span className="px-3 py-1 text-sm">
                第 {pagination.page} 页
              </span>
              <button
                onClick={() => loadUsers(pagination.page + 1)}
                disabled={!pagination.hasMore}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 用户详情弹窗 */}
      {showDetailModal && userDetail && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">用户详细信息</h3>
              <button
                onClick={() => {
                  setShowDetailModal(false)
                  // 恢复滚动位置
                  setTimeout(() => {
                    window.scrollTo(0, scrollPosition)
                  }, 0)
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* 基本信息 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-3">基本信息</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">姓名</p>
                    <p className="font-medium">{userDetail.user.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">邮箱</p>
                    <p className="font-medium">{userDetail.user.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">手机</p>
                    <p className="font-medium">{userDetail.user.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">用户类型</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUserTypeStyle(userDetail.user.user_type)}`}>
                      {getUserTypeText(userDetail.user.user_type)}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500">状态</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(userDetail.user.status)}`}>
                      {getStatusText(userDetail.user.status)}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500">国家/地区</p>
                    <p className="font-medium">{getCountryName(userDetail.user.country)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">时区</p>
                    <p className="font-medium">{userDetail.user.timezone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">注册时间</p>
                    <p className="font-medium">{formatDate(userDetail.user.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* 统计信息 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-3">统计信息</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">总预约数</p>
                    <p className="font-medium">{userDetail.stats.total_bookings || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">教学课时</p>
                    <p className="font-medium">{userDetail.stats.total_teaching_sessions || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">支付次数</p>
                    <p className="font-medium">{userDetail.stats.total_payments || 0}</p>
                  </div>
                </div>
              </div>

              {/* 管理员备注（仅管理端可见） */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-yellow-800 flex items-center">
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    管理员备注
                    <span className="ml-2 text-xs font-normal text-yellow-600">(仅管理员可见)</span>
                  </h4>
                  {!editingAdminNotes && (
                    <button
                      onClick={handleStartEditAdminNotes}
                      className="text-yellow-700 hover:text-yellow-900 text-sm flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      编辑
                    </button>
                  )}
                </div>
                {editingAdminNotes ? (
                  <div className="space-y-3">
                    <textarea
                      value={adminNotesInput}
                      onChange={(e) => setAdminNotesInput(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 focus:outline-none bg-white"
                      placeholder="输入管理员备注信息，例如：用户特殊情况、注意事项等..."
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={handleCancelEditAdminNotes}
                        className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        取消
                      </button>
                      <button
                        onClick={handleSaveAdminNotes}
                        disabled={savingAdminNotes}
                        className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                      >
                        {savingAdminNotes ? '保存中...' : '保存'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-yellow-900 whitespace-pre-wrap min-h-[40px]">
                    {userDetail.user.admin_notes || (
                      <span className="text-yellow-600 italic">暂无备注，点击“编辑”添加</span>
                    )}
                  </div>
                )}
              </div>

              {/* 业务分类管理 (教师特有) */}
              {userDetail.user.user_type === 'teacher' && userDetail.profile && (
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-indigo-800 flex items-center">
                      <TagIcon className="h-4 w-4 mr-2" />
                      学生端业务分类
                      <span className="ml-2 text-xs font-normal text-indigo-600">(仅管理端设置，教师不可见)</span>
                    </h4>
                    {!editingCategories && (
                      <button
                        onClick={handleStartEditCategories}
                        className="text-indigo-700 hover:text-indigo-900 text-sm flex items-center"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        编辑
                      </button>
                    )}
                  </div>
                  {editingCategories ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-4 bg-white p-3 rounded-lg border border-indigo-100">
                        {BUSINESS_CATEGORIES.map(cat => (
                          <label key={cat.id} className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={categoriesInput.includes(cat.id)}
                              onChange={(e) => {
                                const newCats = e.target.checked
                                  ? [...categoriesInput, cat.id]
                                  : categoriesInput.filter(id => id !== cat.id)
                                setCategoriesInput(newCats)
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                            />
                            <span className="ml-2 text-sm text-gray-700">{cat.name}</span>
                          </label>
                        ))}
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={handleCancelEditCategories}
                          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          取消
                        </button>
                        <button
                          onClick={handleSaveCategories}
                          disabled={savingCategories}
                          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                          {savingCategories ? '保存中...' : '保存'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {(() => {
                        const catsData = (userDetail.profile as any).categories
                        let currentCats: string[] = []
                        if (catsData) {
                          try {
                            currentCats = typeof catsData === 'string' ? JSON.parse(catsData) : catsData
                          } catch (e) {
                            console.error('解析分类失败:', e)
                          }
                        }

                        if (!currentCats || currentCats.length === 0) {
                          return <span className="text-indigo-600 italic text-sm">暂未设置分类</span>
                        }

                        return currentCats.map(catId => {
                          const cat = BUSINESS_CATEGORIES.find(c => c.id === catId)
                          return (
                            <span key={catId} className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded text-xs font-medium border border-indigo-200">
                              {cat ? cat.name : catId}
                            </span>
                          )
                        })
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* 详细资料 */}
              {userDetail.profile && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">
                    {userDetail.user.user_type === 'teacher' ? '教师资料' : '学生资料'}
                  </h4>

                  {/* 教师特有资料 */}
                  {userDetail.user.user_type === 'teacher' && (
                    <>
                      {/* 个人照片和视频 */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">个人照片与介绍视频</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* 个人照片 */}
                          {(userDetail.profile as any).profile_photo_url && (
                            <div>
                              <p className="text-xs text-gray-500 mb-2">个人照片</p>
                              <img
                                src={(userDetail.profile as any).profile_photo_url}
                                alt="Profile"
                                className="w-32 h-32 rounded-lg object-cover border border-gray-200"
                              />
                            </div>
                          )}
                          {/* 介绍视频 */}
                          {((userDetail.profile as any).intro_video_url || (userDetail.profile as any).video_url) && (
                            <div className="col-span-1 md:col-span-2">
                              <p className="text-xs text-gray-500 mb-2">介绍视频</p>
                              <video
                                controls
                                preload="metadata"
                                className="w-full max-w-md h-auto rounded-lg border border-gray-200"
                                src={(userDetail.profile as any).intro_video_url || (userDetail.profile as any).video_url}
                              >
                                您的浏览器不支持视频播放
                              </video>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 教学信息 */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">教学信息</h5>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">教育背景</p>
                            <p className="font-medium">{(userDetail.profile as any).education || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">教学经验</p>
                            <p className="font-medium">{(userDetail.profile as any).teaching_experience_years ? `${(userDetail.profile as any).teaching_experience_years}年` : '-'}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-500">教学证书</p>
                            <p className="font-medium">
                              {(userDetail.profile as any).certifications
                                ? (() => {
                                  try {
                                    const cert = typeof (userDetail.profile as any).certifications === 'string'
                                      ? JSON.parse((userDetail.profile as any).certifications)
                                      : (userDetail.profile as any).certifications
                                    if (cert && typeof cert === 'object') {
                                      return cert.selected?.join(', ') + (cert.other ? ` (其他: ${cert.other})` : '')
                                    }
                                    return JSON.stringify(cert)
                                  } catch {
                                    return typeof (userDetail.profile as any).certifications === 'object'
                                      ? JSON.stringify((userDetail.profile as any).certifications)
                                      : (userDetail.profile as any).certifications
                                  }
                                })()
                                : '-'
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 证书文件 */}
                      {(() => {
                        try {
                          const certData = typeof (userDetail.profile as any).certifications === 'string'
                            ? JSON.parse((userDetail.profile as any).certifications)
                            : (userDetail.profile as any).certifications
                          const certFiles = certData?.files || []

                          if (certFiles.length > 0) {
                            return (
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <h5 className="text-sm font-semibold text-gray-700 mb-3">证书文件（{certFiles.length}）</h5>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                  {certFiles.map((fileUrl: string, index: number) => (
                                    <div key={index} className="border border-gray-300 rounded-lg p-3 hover:shadow-md transition-shadow">
                                      <div className="aspect-square w-full bg-gray-100 rounded-lg overflow-hidden mb-2">
                                        {fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                          <img
                                            src={fileUrl}
                                            alt={`证书 ${index + 1}`}
                                            className="w-full h-full object-contain"
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <DocumentTextIcon className="w-12 h-12 text-gray-400" />
                                          </div>
                                        )}
                                      </div>
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center w-full px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                      >
                                        <DocumentTextIcon className="h-4 w-4 mr-1" />
                                        查看文件
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          }
                        } catch (e) {
                          console.error('解析证书数据错误:', e)
                        }
                        return null
                      })()}

                      {/* 继续其他教师信息 */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">语言能力</h5>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">母语</p>
                            <p className="font-medium">{(userDetail.profile as any).native_language || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">教学语言</p>
                            <p className="font-medium">
                              {(userDetail.profile as any).teaching_languages
                                ? (Array.isArray((userDetail.profile as any).teaching_languages)
                                  ? (userDetail.profile as any).teaching_languages.join(', ')
                                  : (() => {
                                    try {
                                      const parsed = JSON.parse((userDetail.profile as any).teaching_languages)
                                      return Array.isArray(parsed) ? parsed.join(', ') : JSON.stringify(parsed)
                                    } catch {
                                      return typeof (userDetail.profile as any).teaching_languages === 'object'
                                        ? JSON.stringify((userDetail.profile as any).teaching_languages)
                                        : (userDetail.profile as any).teaching_languages
                                    }
                                  })())
                                : '-'
                              }
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-500">教学风格/专长</p>
                            <p className="font-medium">
                              {(userDetail.profile as any).specialties
                                ? (Array.isArray((userDetail.profile as any).specialties)
                                  ? (userDetail.profile as any).specialties.join(', ')
                                  : (() => {
                                    try {
                                      const parsed = JSON.parse((userDetail.profile as any).specialties)
                                      return Array.isArray(parsed) ? parsed.join(', ') : JSON.stringify(parsed)
                                    } catch {
                                      return typeof (userDetail.profile as any).specialties === 'object'
                                        ? JSON.stringify((userDetail.profile as any).specialties)
                                        : (userDetail.profile as any).specialties
                                    }
                                  })())
                                : '-'
                              }
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">期望时薪(25分钟)</p>
                            <p className="font-medium text-green-600 font-semibold">
                              {(userDetail.profile as any).expected_hourly_rate
                                ? `$${(userDetail.profile as any).expected_hourly_rate}`
                                : '-'
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 费率设置（可编辑） */}
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-semibold text-green-800 flex items-center">
                            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                            费率设置
                            <span className="ml-2 text-xs font-normal text-green-600">(管理员可编辑)</span>
                          </h5>
                          {!editingRates && (userDetail.user as any).approval_status === 'approved' && (
                            <button
                              onClick={handleStartEditRates}
                              className="text-green-700 hover:text-green-900 text-sm flex items-center"
                            >
                              <PencilIcon className="h-4 w-4 mr-1" />
                              编辑
                            </button>
                          )}
                        </div>
                        {editingRates ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-green-800 mb-1">
                                  设置课时费 ($/25分钟)
                                  <span className="block text-xs font-normal text-green-600">向教师付费</span>
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={ratesForm.hourly_rate}
                                  onChange={(e) => setRatesForm({ ...ratesForm, hourly_rate: parseFloat(e.target.value) || 0 })}
                                  className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none bg-white"
                                  placeholder="输入课时费"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-green-800 mb-1">
                                  学生收费 (¥/25分钟)
                                  <span className="block text-xs font-normal text-green-600">向学生收费</span>
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={ratesForm.student_rate_per_25min}
                                  onChange={(e) => setRatesForm({ ...ratesForm, student_rate_per_25min: parseFloat(e.target.value) || 0 })}
                                  className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none bg-white"
                                  placeholder="输入学生收费"
                                />
                              </div>
                            </div>
                            {/* 特价教师开关 */}
                            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={ratesForm.is_special_price}
                                  onChange={(e) => setRatesForm({ ...ratesForm, is_special_price: e.target.checked })}
                                  className="rounded border-orange-300 text-orange-600 focus:ring-orange-500 h-4 w-4"
                                />
                                <span className="ml-2 text-sm font-medium text-orange-800">特价教师</span>
                                <span className="ml-2 text-xs text-orange-600">（特价教师的课程不享受学生专属折扣）</span>
                              </label>
                            </div>
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={handleCancelEditRates}
                                className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                              >
                                取消
                              </button>
                              <button
                                onClick={handleSaveRates}
                                disabled={savingRates}
                                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                              >
                                {savingRates ? '保存中...' : '保存'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-green-700">设置课时费 ($/25分钟)</p>
                                <p className="text-xs text-green-600 mb-1">向教师付费</p>
                                <p className="font-semibold text-green-900 text-lg">
                                  {(userDetail.profile as any).hourly_rate != null
                                    ? `$${(userDetail.profile as any).hourly_rate}`
                                    : <span className="text-green-600 italic text-sm">未设置</span>
                                  }
                                </p>
                              </div>
                              <div>
                                <p className="text-green-700">学生收费 (¥/25分钟)</p>
                                <p className="text-xs text-green-600 mb-1">向学生收费</p>
                                <p className="font-semibold text-green-900 text-lg">
                                  {(userDetail.profile as any).student_rate_per_25min != null
                                    ? `¥${(userDetail.profile as any).student_rate_per_25min}`
                                    : <span className="text-green-600 italic text-sm">未设置</span>
                                  }
                                </p>
                              </div>
                            </div>
                            {/* 特价教师状态显示 */}
                            {(userDetail.profile as any).is_special_price === 1 && (
                              <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                                <span className="text-sm font-medium text-orange-800">🏷️ 特价教师</span>
                                <span className="ml-2 text-xs text-orange-600">（课程不享受学生专属折扣）</span>
                              </div>
                            )}
                          </>
                        )}
                        {(userDetail.user as any).approval_status !== 'approved' && (
                          <p className="mt-2 text-xs text-green-600 italic">
                            注意：教师审核通过后才能编辑费率
                          </p>
                        )}
                      </div>

                      {/* 个人简介 */}
                      {(userDetail.profile as any).bio && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">个人简介</h5>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">
                            {(userDetail.profile as any).bio}
                          </p>
                        </div>
                      )}

                      {/* 支付信息 */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">支付信息</h5>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">支付方式</p>
                            <p className="font-medium">{(userDetail.profile as any).payment_method || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">收款账户</p>
                            <p className="font-medium">{(userDetail.profile as any).payment_account || '-'}</p>
                          </div>
                        </div>
                      </div>

                      {/* 视频会议ID */}
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h5 className="text-sm font-semibold text-blue-900 mb-3">视频会议信息</h5>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-blue-700">腾讯会议ID</p>
                            <p className="font-medium text-blue-900">
                              {(userDetail.profile as any).tencent_meeting_id || '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-blue-700">Zoom会议ID</p>
                            <p className="font-medium text-blue-900">
                              {(userDetail.profile as any).zoom_personal_meeting_id || '-'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 其他信息 */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">其他信息</h5>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {Object.entries(userDetail.profile)
                            .filter(([key, value]) => {
                              // 过滤掉不需要显示的字段
                              const excludeKeys = [
                                // 已在其他区域显示的字段
                                'tencent_meeting_id', 'zoom_personal_meeting_id', 'education',
                                'teaching_experience_years', 'certifications', 'native_language',
                                'teaching_languages', 'specialties', 'expected_hourly_rate',
                                'bio', 'payment_method', 'payment_account', 'profile_photo_url',
                                'intro_video_url', 'video_url', 'hourly_rate', 'student_rate_per_25min',
                                'specialties_courses', 'target_student_languages', 'booking_stats',
                                'recent_reviews', 'file_uploads', 'video_info',
                                // 系统字段，不需要显示
                                'id', 'user_id', 'created_at', 'updated_at', 'approval_status',
                                'rejection_reason', 'admin_notes', 'email', 'full_name', 'phone',
                                'avatar_url', 'status', 'user_type', 'email_verified', 'phone_verified',
                                'password_hash', 'confirmation_token', 'confirmed_at', 'last_login',
                                'last_login_ip', 'login_count', 'failed_login_attempts', 'user_admin_notes',
                                // 其他无意义的字段
                                'password', 'token', 'hash', 'salt', 'video_duration', 'video_size'
                              ]
                              // 过滤掉排除字段和空值
                              return !excludeKeys.includes(key) && value != null && value !== ''
                            })
                            .map(([key, value]) => {
                              // 字段名称中文映射
                              const fieldNameMap: Record<string, string> = {
                                'country': '国家/地区',
                                'timezone': '时区',
                                'gender': '性别',
                                'age': '年龄',
                                'birthday': '生日',
                                'birth_date': '出生日期',
                                'speaks_chinese': '是否会中文',
                                'chinese_proficiency': '中文水平',
                                'teacher_level': '教师等级',
                                'rating': '评分',
                                'total_reviews': '评价数',
                                'total_lessons': '课程数',
                                'completion_rate': '完成率',
                                'balance_hours': '当前余额 (课时)',
                                'total_consumed_hours': '已消耗课时',
                                'has_video': '有视频',
                                'currency': '货币',
                                'target_student_languages': '目标学生语言',
                                'categories': '教学类别',
                                'approved_at': '审核通过时间',
                                'rejected_at': '拒绝时间',
                                'approved_by': '审核人',
                                'last_login_at': '最后登录时间',
                                'phone_verified': '手机已验证',
                                'email_verified': '邮箱已验证',
                                'education': '教育背景',
                                'certifications': '教学证书',
                                'native_language': '母语',
                                'teaching_languages': '教学语言',
                                'certifications_verified': '资质是否已验证',
                                'total_earnings': '累计总收益',
                                'current_balance': '当前余额',
                                'withdrawn_amount': '已提现金额',
                                'is_available': '是否在职/可用',
                                'privacy_agreement_accepted': '隐私协议已签署',
                                'preferred_video_platform': '首选视频平台',
                                'created_at': '注册时间'
                              }
                              const displayName = fieldNameMap[key] || key.replace(/_/g, ' ')

                              // 格式化值
                              let displayValue: React.ReactNode = '-'
                              if (value != null && value !== '') {
                                if (typeof value === 'boolean') {
                                  displayValue = value ? '是' : '否'
                                } else if (Array.isArray(value)) {
                                  displayValue = value.join(', ')
                                } else if (typeof value === 'object') {
                                  // 对象类型尝试美化显示
                                  try {
                                    const entries = Object.entries(value)
                                    if (entries.length > 0) {
                                      displayValue = entries
                                        .filter(([, v]) => v != null && v !== '')
                                        .map(([k, v]) => `${k}: ${v}`)
                                        .join(', ')
                                    }
                                  } catch {
                                    displayValue = String(value)
                                  }
                                } else if (typeof value === 'number') {
                                  if (key.includes('hours')) {
                                    displayValue = `${value} 小时`
                                  } else if (key.includes('rate') && !key.includes('completion')) {
                                    displayValue = `$${value}`
                                  } else if (key === 'completion_rate') {
                                    displayValue = `${(value * 100).toFixed(1)}%`
                                  } else {
                                    displayValue = String(value)
                                  }
                                } else if (typeof value === 'string') {
                                  // 检查是否是日期字符串
                                  if (key.includes('_at') || key.includes('date') || key.includes('time') || key === 'birthday') {
                                    displayValue = formatDate(value)
                                  } else if (key === 'country') {
                                    displayValue = getCountryName(value)
                                  } else if (key === 'gender') {
                                    const genderMap: Record<string, string> = { 'male': '男', 'female': '女', 'other': '其他' }
                                    displayValue = genderMap[value as string] || value
                                  } else if (key === 'speaks_chinese') {
                                    displayValue = value === 'yes' ? '是' : value === 'no' ? '否' : value
                                  } else if (key === 'chinese_proficiency') {
                                    const profMap: Record<string, string> = { 'basic': '基础', 'intermediate': '中级', 'advanced': '高级', 'native': '母语' }
                                    displayValue = profMap[value as string] || value
                                  } else {
                                    displayValue = value
                                  }
                                } else {
                                  displayValue = String(value)
                                }
                              }

                              return (
                                <div key={key}>
                                  <p className="text-gray-500 capitalize">{displayName}</p>
                                  <p className="font-medium">{displayValue}</p>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    </>
                  )}

                  {/* 学生资料 - 美化显示 */}
                  {userDetail.user.user_type === 'student' && (
                    <div className="space-y-4">
                      {/* 会员等级和折扣设置 */}
                      <StudentMemberSettings
                        studentId={userDetail.user.id}
                        currentLevelId={(userDetail.profile as any).member_level_id}
                        currentLevelName={(userDetail.profile as any).member_level_name}
                        currentLevelIcon={(userDetail.profile as any).member_level_icon}
                        currentLevelColor={(userDetail.profile as any).member_level_color}
                        discountThreshold={(userDetail.profile as any).discount_threshold || 0}
                        discountRate={(userDetail.profile as any).discount_rate || 1.0}
                        onUpdate={() => loadUserDetail(userDetail.user.id)}
                      />

                      {/* 学生基本资料 */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {Object.entries(userDetail.profile)
                            .filter(([key, value]) => {
                              // 过滤掉不需要显示的字段
                              const excludeKeys = [
                                'id', 'user_id', 'created_at', 'updated_at',
                                'email', 'full_name', 'phone', 'avatar_url',
                                'status', 'user_type', 'email_verified', 'phone_verified',
                                'password_hash', 'confirmation_token', 'confirmed_at',
                                'last_login', 'last_login_ip', 'login_count',
                                'failed_login_attempts', 'password', 'token', 'hash', 'salt',
                                // 会员等级和折扣字段已在上方显示
                                'member_level_id', 'member_level_name', 'member_level_name_en',
                                'member_level_icon', 'member_level_color',
                                'discount_threshold', 'discount_rate'
                              ]

                              // 如果存在 _display 版本的字段，则隐藏原始字段
                              const hasDisplayVersion = userDetail.profile.hasOwnProperty(`${key}_display`)
                              if (hasDisplayVersion && !key.endsWith('_display')) {
                                return false
                              }

                              return !excludeKeys.includes(key) && value != null && value !== ''
                            })
                            .map(([key, value]) => {
                              // 字段名称中文映射
                              const fieldNameMap: Record<string, string> = {
                                'country': '国家/地区',
                                'timezone': '时区',
                                'gender': '性别',
                                'age': '年龄',
                                'birthday': '生日',
                                'english_name': '英文名',
                                'english_level': '英语水平',
                                'occupation': '职业',
                                'grade': '年级',
                                'balance_hours': '剩余金额',
                                'total_hours_consumed': '已上次数',
                                'total_spent': '累计消费',
                                'average_rating': '平均评分',
                                'total_reviews': '评价总数',
                                'cancellation_count_today': '今日取消次数',
                                'cancellation_count_month': '本月取消次数',
                                'cancellation_count_total': '累计取消次数',
                                'last_cancellation_date': '最后取消日期',
                                'native_language': '母语',
                                'target_language': '目标语言',
                                'learning_goals': '学习目标',
                                'preferred_teachers': '偏好教师',
                                'english_level_display': '英语水平',
                                'gender_display': '性别',
                                'occupation_display': '职业',
                                'grade_display': '年级'
                              }
                              const displayName = fieldNameMap[key] || key.replace(/_/g, ' ')

                              // 格式化值
                              let displayValue: React.ReactNode = '-'
                              if (value != null && value !== '') {
                                if (typeof value === 'boolean') {
                                  displayValue = value ? '是' : '否'
                                } else if (Array.isArray(value)) {
                                  displayValue = value.join(', ')
                                } else if (typeof value === 'object') {
                                  try {
                                    const entries = Object.entries(value)
                                    if (entries.length > 0) {
                                      displayValue = entries
                                        .filter(([, v]) => v != null && v !== '')
                                        .map(([k, v]) => `${k}: ${v}`)
                                        .join(', ')
                                    }
                                  } catch {
                                    displayValue = String(value)
                                  }
                                } else if (typeof value === 'number') {
                                  // balance_hours 现在存储的是金额，不再是课时
                                  if (key === 'balance_hours') {
                                    displayValue = `￥${value.toFixed(2)}`
                                  } else if (key === 'total_spent') {
                                    // 累计消费显示为人民币格式
                                    displayValue = `￥${value.toFixed(2)}`
                                  } else if (key === 'total_hours_consumed') {
                                    // 已上次数显示为整数次数
                                    displayValue = `${Math.floor(value)} 次`
                                  } else if (key.includes('hours') && key !== 'balance_hours' && key !== 'total_hours_consumed') {
                                    displayValue = `${value} 小时`
                                  } else if (key.includes('amount') || key.includes('price')) {
                                    displayValue = `$${value}`
                                  } else if (key === 'average_rating') {
                                    displayValue = `${value.toFixed(1)}/5.0`
                                  } else {
                                    displayValue = String(value)
                                  }
                                } else if (typeof value === 'string') {
                                  if (key.includes('_at') || key.includes('date')) {
                                    displayValue = formatDate(value)
                                  } else if (key === 'country') {
                                    displayValue = getCountryName(value)
                                  } else if (key === 'gender') {
                                    displayValue = value === 'male' ? '男' : value === 'female' ? '女' : value
                                  } else if (key.endsWith('_display')) {
                                    // 对于 _display 字段，直接显示后端已翻译的值
                                    displayValue = value
                                  } else if (key === 'english_level') {
                                    // 英语水平字段映射
                                    const levelMap: Record<string, string> = {
                                      'pre_a1_starters': '零基础入门',
                                      'pre_a1_movers': '启蒙探索者',
                                      'a1': '初级沟通者',
                                      'a2': '中级沟通者',
                                      'b1': '高级运用者',
                                      'b2_plus': '熟练使用者'
                                    }
                                    displayValue = levelMap[value] || value
                                  } else if (key === 'occupation') {
                                    // 职业字段映射
                                    const occupationMap: Record<string, string> = {
                                      'child': '少儿',
                                      'primary': '小学生',
                                      'junior': '初中生',
                                      'senior': '高中生',
                                      'adult': '成人'
                                    }
                                    displayValue = occupationMap[value] || value
                                  } else if (key === 'grade') {
                                    // 年级字段映射
                                    const gradeMap: Record<string, string> = {
                                      'preschool': '学前',
                                      'grade1': '一年级',
                                      'grade2': '二年级',
                                      'grade3': '三年级',
                                      'grade4': '四年级',
                                      'grade5': '五年级',
                                      'grade6': '六年级',
                                      'junior1': '初一',
                                      'junior2': '初二',
                                      'junior3': '初三',
                                      'junior4': '初四',
                                      'senior1': '高一',
                                      'senior2': '高二',
                                      'senior3': '高三',
                                      'senior4': '高四',
                                      'college1': '大一',
                                      'college2': '大二',
                                      'college3': '大三',
                                      'college4': '大四',
                                      'graduate': '研究生',
                                      'phd': '博士生'
                                    }
                                    displayValue = gradeMap[value] || value
                                  } else {
                                    displayValue = value
                                  }
                                } else {
                                  displayValue = String(value)
                                }
                              }

                              return (
                                <div key={key}>
                                  <p className="text-gray-500 capitalize">{displayName}</p>
                                  <p className="font-medium">{displayValue}</p>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              {/* 教师审核按钮 */}
              {userDetail.user.user_type === 'teacher' && (userDetail.user as any).approval_status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      setSelectedUser(userDetail.user)
                      setShowDetailModal(false)
                      setShowApproveModal(true)
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    批准申请
                  </button>
                  <button
                    onClick={() => {
                      setSelectedUser(userDetail.user)
                      setShowDetailModal(false)
                      setShowRejectModal(true)
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    拒绝申请
                  </button>
                </>
              )}

              <button
                onClick={() => {
                  setSelectedUser(userDetail.user)
                  setNewStatus(userDetail.user.status === 'active' ? 'suspended' : 'active')
                  setShowDetailModal(false)
                  setShowStatusModal(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                更改状态
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 状态更改确认弹窗 */}
      {showStatusModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">确认更改用户状态</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                用户: <span className="font-medium">{selectedUser.full_name}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                当前状态:
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${getStatusStyle(selectedUser.status)}`}>
                  {getStatusText(selectedUser.status)}
                </span>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  新状态
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                >
                  <option value="active">活跃</option>
                  <option value="suspended">已暂停</option>
                  <option value="banned">已封禁</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                确认更改
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批准教师申请弹窗 */}
      {showApproveModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">批准教师申请</h3>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  申请人: <span className="font-medium text-gray-900">{selectedUser.full_name}</span>
                </p>
                <p className="text-sm text-gray-600">
                  邮箱: <span className="font-medium text-gray-900">{selectedUser.email}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  教师等级 *
                </label>
                <select
                  value={approveForm.teacher_level}
                  onChange={(e) => setApproveForm({ ...approveForm, teacher_level: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="1">1 - 初级教师</option>
                  <option value="2">2 - 中级教师</option>
                  <option value="3">3 - 高级教师</option>
                  <option value="4">4 - 资深教师</option>
                  <option value="5">5 - 特级教师</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  设定课时费 ($/25分钟)
                </label>
                <input
                  type="number"
                  min="0"
                  step="10"
                  value={approveForm.set_hourly_rate}
                  onChange={(e) => setApproveForm({ ...approveForm, set_hourly_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="留空使用教师设置的费率"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  业务分类 (可多选)
                </label>
                <div className="flex flex-wrap gap-3">
                  {BUSINESS_CATEGORIES.map(cat => (
                    <label key={cat.id} className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={approveForm.categories.includes(cat.id)}
                        onChange={(e) => {
                          const newCats = e.target.checked
                            ? [...approveForm.categories, cat.id]
                            : approveForm.categories.filter(id => id !== cat.id)
                          setApproveForm({ ...approveForm, categories: newCats })
                        }}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-4 w-4"
                      />
                      <span className="ml-2 text-sm text-gray-700">{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  管理员备注
                </label>
                <textarea
                  value={approveForm.admin_notes}
                  onChange={(e) => setApproveForm({ ...approveForm, admin_notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="可选，内部备注"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApproveModal(false)
                  setApproveForm({ teacher_level: 1, admin_notes: '', set_hourly_rate: 0, categories: [] })
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                取消
              </button>
              <button
                onClick={handleApproveTeacher}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                确认批准
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 拒绝教师申请弹窗 */}
      {showRejectModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">拒绝教师申请</h3>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  申请人: <span className="font-medium text-gray-900">{selectedUser.full_name}</span>
                </p>
                <p className="text-sm text-gray-600">
                  邮箱: <span className="font-medium text-gray-900">{selectedUser.email}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  拒绝原因 *
                </label>
                <textarea
                  value={rejectForm.rejection_reason}
                  onChange={(e) => setRejectForm({ ...rejectForm, rejection_reason: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="请详细说明拒绝原因，这将发送给申请人"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  管理员备注
                </label>
                <textarea
                  value={rejectForm.admin_notes}
                  onChange={(e) => setRejectForm({ ...rejectForm, admin_notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="可选，内部备注"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allow_reapply"
                  checked={rejectForm.allow_reapply}
                  onChange={(e) => setRejectForm({ ...rejectForm, allow_reapply: e.target.checked })}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="allow_reapply" className="ml-2 block text-sm text-gray-700">
                  允许重新申请
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectForm({ rejection_reason: '', admin_notes: '', allow_reapply: true })
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                取消
              </button>
              <button
                onClick={handleRejectTeacher}
                disabled={loading || !rejectForm.rejection_reason}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                确认拒绝
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加用户弹窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">添加用户</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  用户类型 <span className="text-red-500">*</span>
                </label>
                <select
                  value={createForm.user_type}
                  onChange={e => setCreateForm({ ...createForm, user_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="student">🎓 学生</option>
                  <option value="teacher">🏫 教师</option>
                  <option value="admin">👤 管理员</option>
                  <option value="referrer">💼 推广人员</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createForm.full_name}
                  onChange={e => setCreateForm({ ...createForm, full_name: e.target.value })}
                  placeholder="请输入姓名"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {(createForm.user_type === 'teacher' || createForm.user_type === 'admin' || createForm.user_type === 'referrer') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              {createForm.user_type === 'student' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    手机号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={createForm.phone}
                    onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
                    placeholder="请输入登录手机号"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  设置密码
                </label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="留空默认 123456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2" />
                  <p className="text-xs text-blue-800">
                    管理员手动创建用户后，用户可以使用上述信息直接登录。
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={async () => {
                  if (!createForm.full_name) {
                    toast.error('请填写姓名')
                    return
                  }
                  if (createForm.user_type === 'student' && !createForm.phone) {
                    toast.error('学生必须填写手机号')
                    return
                  }
                  if ((createForm.user_type === 'teacher' || createForm.user_type === 'admin') && !createForm.email) {
                    toast.error('教师或管理员必须填写邮箱')
                    return
                  }

                  try {
                    setLoading(true)
                    await usersApi.createUser(createForm)
                    toast.success('用户创建成功！')
                    setShowCreateModal(false)
                    setCreateForm({ email: '', phone: '', full_name: '', password: '', user_type: 'student' })
                    loadUsers()
                  } catch (e: any) {
                    toast.error(e.message || '创建失败')
                  } finally {
                    setLoading(false)
                  }
                }}
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                创建用户
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
